import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { ArrowLeft, Phone, MessageCircle, Clock, MapPin, User, Info, ListOrdered, Maximize } from "lucide-react";
import { rundownStatus, RundownBadge } from "@/lib/rundownStatus";

const waLink = (phone) => { let n = String(phone || "").replace(/[^0-9]/g, ""); if (n.startsWith("0")) n = "62" + n.slice(1); return `https://wa.me/${n}`; };
const toMin = (t) => { const [h, m] = String(t || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };

export default function ClientEventDayMode() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [now, setNow] = useState(new Date());
  const [bgIdx, setBgIdx] = useState(0);
  const [tab, setTab] = useState("rundown");

  useEffect(() => {
    api.get("/portal/me").then((r) => setD(r.data)).catch(() => setD(false));
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const gallery = useMemo(() => (d && d.media ? d.media.filter((m) => m.type === "timeline") : []), [d]);
  useEffect(() => {
    if (gallery.length < 2) return;
    const t = setInterval(() => setBgIdx((i) => (i + 1) % gallery.length), 7000);
    return () => clearInterval(t);
  }, [gallery.length]);

  const rundown = useMemo(() => (d && d.rundown ? [...d.rundown].sort((a, b) => toMin(a.time) - toMin(b.time) || (a.order || 0) - (b.order || 0)) : []), [d]);

  const isSameDay = (iso) => { if (!iso) return false; const a = new Date(iso), b = now; return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); };
  const { current, next } = useMemo(() => {
    if (!isSameDay(d && d.event && d.event.wedding_date)) return { current: null, next: rundown[0] || null };
    const mins = now.getHours() * 60 + now.getMinutes();
    let cur = null, nxt = null;
    for (let i = 0; i < rundown.length; i++) {
      const start = toMin(rundown[i].time);
      const end = rundown[i].end_time ? toMin(rundown[i].end_time) : (i + 1 < rundown.length ? toMin(rundown[i + 1].time) : start + 60);
      if (start <= mins && mins < end) cur = rundown[i];
      else if (mins < start && !nxt) nxt = rundown[i];
    }
    return { current: cur, next: nxt };
  }, [now, rundown, d]);

  const clock = now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" }).replace(/[:.]/g, ".");

  if (d === null) return <div className="min-h-screen bg-[#1a0409] text-white flex items-center justify-center">Memuat...</div>;
  if (d === false || !d.event) return (
    <div className="min-h-screen bg-[#1a0409] text-white flex flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="text-white/70">Data acara belum tersedia.</p>
      <button onClick={() => navigate("/portal")} className="text-gold-light underline" data-testid="daymode-back">Kembali ke Portal</button>
    </div>
  );

  const ev = d.event;
  const contacts = ((d.pms || []).map((p) => ({ label: p.role || "Wedding Organizer", name: p.member_name, phone: p.phone, wa: true }))).filter((c) => c.name);

  const RundownList = ({ compact }) => (
    <div className="space-y-2" data-testid="daymode-rundown">
      {rundown.length === 0 && <p className="text-white/50 text-sm">Rundown belum tersedia.</p>}
      {rundown.map((r) => {
        const st = rundownStatus(ev, r, now);
        const active = st === "ON GOING";
        return (
          <div key={r.id} className={`flex items-center gap-3 rounded-xl px-3 sm:px-4 py-2.5 transition-colors ${active ? "bg-gold/25 border border-gold/50" : "bg-white/5 border border-transparent"}`}>
            {active && <span className="w-2 h-2 rounded-full bg-gold shrink-0" />}
            <span className="font-mono text-xs sm:text-sm text-gold-light whitespace-nowrap">{r.time}{!compact && r.end_time ? ` - ${r.end_time}` : ""}</span>
            <span className="flex-1 text-sm text-white truncate">{r.activity}</span>
            {st && <RundownBadge status={st} />}
            {r.pic && <span className="text-[11px] text-white/50 whitespace-nowrap hidden sm:block">PIC {r.pic}</span>}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#1a0409] text-white relative overflow-hidden">
      {/* Background slideshow from client gallery */}
      {gallery.length > 0 && (
        <div className="fixed inset-0 z-0" data-testid="daymode-bg">
          {gallery.map((m, i) => (
            <div key={m.id} className={`absolute inset-0 transition-opacity ${i === bgIdx ? "opacity-100" : "opacity-0"}`} style={{ transitionDuration: "1500ms" }}>
              <img src={fileUrl(m.storage_path)} alt="" className={`w-full h-full object-cover ${i === bgIdx ? "animate-ken-burns" : ""}`} />
            </div>
          ))}
          <div className="absolute inset-0 bg-[#1a0409]/80" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a0409]/70 via-[#1a0409]/60 to-[#1a0409]/90" />
        </div>
      )}

      <div className="relative z-10 max-w-4xl mx-auto px-5 py-6 pb-24 sm:pb-8">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/portal")} className="flex items-center gap-1 text-sm text-gold-light hover:text-white" data-testid="daymode-back"><ArrowLeft className="w-4 h-4" />Kembali ke Portal</button>
          <button onClick={() => { const el = document.documentElement; if (el.requestFullscreen) el.requestFullscreen().catch(() => {}); }} className="p-2 rounded-full hover:bg-white/10 text-gold-light" data-testid="daymode-fullscreen" aria-label="Layar penuh"><Maximize className="w-4 h-4" /></button>
        </div>

        <div className="text-center mb-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold-light">Event Day Mode</p>
          <h1 className="font-heading text-4xl sm:text-5xl mt-1">{ev.groom} & {ev.bride}</h1>
          <p className="text-sm text-white/70 mt-1">{fmtDate(ev.wedding_date)}</p>
          <p className="font-mono text-5xl sm:text-7xl mt-3 tabular-nums tracking-tight" data-testid="daymode-clock">{clock}</p>
        </div>

        {/* NOW / NEXT */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" id="daymode-informasi">
          <div className="rounded-2xl bg-white/10 backdrop-blur p-5 border border-gold/20" data-testid="daymode-now">
            <p className="text-xs uppercase tracking-[0.2em] text-gold-light mb-2">Now</p>
            {current ? (
              <>
                <p className="font-mono text-2xl text-gold-light">{current.time}</p>
                <h2 className="font-heading text-2xl sm:text-3xl mt-1">{current.activity}</h2>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-white/70">
                  {current.pic && <span className="flex items-center gap-1"><User className="w-4 h-4" />{current.pic}</span>}
                  {current.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{current.location}</span>}
                </div>
              </>
            ) : <p className="text-white/60 mt-3">Belum ada aktivitas berjalan.</p>}
          </div>
          <div className="rounded-2xl bg-gold/15 backdrop-blur p-5 border border-gold/30" data-testid="daymode-next">
            <p className="text-xs uppercase tracking-[0.2em] text-gold-light mb-2">Next</p>
            {next ? (
              <>
                <p className="font-mono text-2xl text-gold-light">{next.time}</p>
                <h2 className="font-heading text-2xl sm:text-3xl mt-1">{next.activity}</h2>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-white/70">
                  {next.pic && <span className="flex items-center gap-1"><User className="w-4 h-4" />{next.pic}</span>}
                  {next.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{next.location}</span>}
                </div>
              </>
            ) : <p className="text-white/60 mt-3">Tidak ada aktivitas berikutnya.</p>}
          </div>
        </div>

        {/* Full rundown */}
        <div className={`mb-8 ${tab !== "rundown" ? "hidden sm:block" : ""}`} id="daymode-rundown-sec">
          <p className="text-xs uppercase tracking-[0.2em] text-gold-light mb-3 flex items-center gap-2"><Clock className="w-4 h-4" />Full Rundown</p>
          <RundownList />
        </div>

        {/* Emergency contacts */}
        <div className={`${tab !== "kontak" ? "hidden sm:block" : ""}`} id="daymode-kontak">
          <p className="text-xs uppercase tracking-[0.2em] text-gold-light mb-3">Emergency Contacts</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {contacts.length === 0 && <p className="text-white/50 text-sm">Belum ada kontak.</p>}
            {contacts.map((c, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl bg-white/5 px-4 py-3 border border-white/10" data-testid="daymode-contact">
                <div className="min-w-0"><p className="text-sm font-semibold truncate">{c.name}</p><p className="text-xs text-white/50">{c.label}</p></div>
                {c.phone && (
                  <div className="flex gap-2 shrink-0">
                    <a href={`tel:${c.phone}`} aria-label="Call" className="w-9 h-9 rounded-full bg-burgundy flex items-center justify-center hover:bg-burgundy-dark" data-testid="daymode-call"><Phone className="w-4 h-4" /></a>
                    {c.wa && <a href={waLink(c.phone)} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center hover:bg-emerald-700" data-testid="daymode-wa"><MessageCircle className="w-4 h-4" /></a>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <nav className="sm:hidden fixed bottom-0 inset-x-0 z-20 bg-[#2a070d]/95 backdrop-blur border-t border-gold/20 flex" data-testid="daymode-bottomnav">
        {[["rundown", "Rundown", ListOrdered], ["informasi", "Informasi", Info], ["kontak", "Kontak", Phone]].map(([key, lbl, Icon]) => (
          <button key={key} onClick={() => { setTab(key); const el = document.getElementById(key === "informasi" ? "daymode-informasi" : key === "kontak" ? "daymode-kontak" : "daymode-rundown-sec"); el?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
            className={`flex-1 py-3 flex flex-col items-center gap-1 text-[11px] ${tab === key ? "text-gold-light" : "text-white/60"}`} data-testid={`daymode-nav-${key}`}>
            <Icon className="w-5 h-5" />{lbl}
          </button>
        ))}
      </nav>
    </div>
  );
}
